import React, { Fragment, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, map } from 'lodash-es';

import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { useOnScreen } from 'site-modules/shared/hooks/use-on-screen';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { ComparatorModel } from 'client/data/models/comparator';
import { buildTrimsInventoryPath, InventoryAggregateModel } from 'client/data/models/inventory-aggregate';
import { buildRecommendedTrimPath, EditorialReviewModel } from 'client/data/models/editorial-review';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { tcoMap, trimsFeaturesMap } from 'client/data/models/car-features-map';
import { getColumnIdsString, getTCOIdsString, hasFeatureValue } from 'site-modules/comparator/utils/comparator';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { numberWithCommasForString } from 'site-modules/shared/utils/string';
import { withHash } from 'site-modules/shared/utils/comparator/comparator-hash';
import { SUB_NAV_SECTIONS_MAP } from 'site-modules/comparator/constants/sections-map';
import { NAV_LIST_CUSTOM_EVENT } from 'site-modules/comparator/constants/comparator';
import { isUsedState } from 'site-modules/shared/utils/publication-states';
import { getStyleName } from 'site-modules/shared/utils/features-specs/utils';

// Components
import { FeatureField } from 'site-modules/comparator/components/feature-field/feature-field';
import { CarColorSection } from 'site-modules/shared/components/comparator/car-color-section/car-color-section';
import { ContentTable } from 'site-modules/shared/components/comparator/content-table/content-table';
import { ComparatorAnchorNav } from 'site-modules/comparator/components/comparator-anchor-nav/comparator-anchor-nav';
import { AnimatedSticky } from 'site-modules/shared/components/animated-sticky/animated-sticky';
import { DrivetrainFeatureField } from 'site-modules/comparator/components/drivetrain-feature-field/drivetrain-feature-field';
import { TrimCard } from 'site-modules/comparator/components/trim-card/trim-card';
import { TrimAddCard } from 'site-modules/comparator/components/trim-card/trim-add-card';
import { HideSimilaritiesSwitch } from 'site-modules/comparator/components/hide-similarities-switch/hide-similarities-switch';
import { ScrollArrows } from 'site-modules/comparator/components/scroll-arrows/scroll-arrows';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';

import './trims-comparator-table.scss';

const NOT_AVAILABLE = 'Not Available';

const getFeatureColumns = ({ evComparableFeatures, columns, feature }) =>
  columns.map((column, index) => ({
    column,
    feature: {
      ...feature,
      ...(get(evComparableFeatures, `[${index}].isElectric`) ? feature.electric : null),
      ...(get(evComparableFeatures, `[${index}].isPluginElectric`) ? feature.hybrid : null),
    },
  }));

function TrimsComparatorTableUI({
  title,
  styles = [],
  columns = [],
  removedTrims = null,
  navList,
  arrows,
  onArrowClick,
  setTrimStyle,
  onAddTrim,
  onRemoveTrim,
  onStickyStateChange,
  evComparableFeatures = null,
  pricing = null,
  tco = null,
  colors = null,
  trimsInventoryCount = null,
  recommendedTrim = null,
  comparatorHeaderRef = null,
  comparatorHeaderStickyRef = null,
  customOffset = null,
  isSmall = false,
  comparableFeatures = null,
}) {
  const [hideSimilar, toggleHideSimilar] = useToggle(false);
  const tableRef = useRef();
  const [isTableVisible] = useOnScreen(tableRef);

  const contentData = useMemo(() => {
    const hasEvSection =
      !!evComparableFeatures &&
      evComparableFeatures.some(
        evComparableFeature => get(evComparableFeature, 'isElectric') || get(evComparableFeature, 'isPluginElectric')
      );

    const stickyCarHeaderRenderMap = [
      {
        alternateFeaturesBgColor: false,
        sectionClassName: 'trim-cards-section pos-r',
        features: [
          {
            rowClassName: 'trim-cards-row bg-cool-gray-90',
            data: columns.map(({ styleId }, index) =>
              styleId ? (
                <TrimCard
                  trimName={styles?.[index]?.trim}
                  engine={getStyleName(styles?.[index]?.name)?.engine}
                  isRecommended={makeNiceName(styles?.[index]?.trim) === recommendedTrim}
                  onRemoveTrim={onRemoveTrim}
                  isSticky
                />
              ) : (
                <TrimAddCard onAddTrim={onAddTrim} removedTrims={removedTrims} isSticky />
              )
            ),
          },
        ],
      },
    ];

    const renderMap = [
      {
        isThead: true,
        features: [
          {
            srOnly: true,
            data: columns.map((column, index) => {
              const modelName = get(styles, `[${index}].modelName`);
              const trim = get(styles, `[${index}].trim`);

              return (
                <Fragment>
                  {modelName} {trim}
                </Fragment>
              );
            }),
          },
        ],
      },
      {
        id: 'edm-comparator-pricing',
        ref: comparatorHeaderRef,
        sectionClassName: 'trim-cards-section pos-r',
        alternateFeaturesBgColor: false,
        features: [
          {
            rowClassName: 'trim-cards-row bg-cool-gray-90',
            data: columns.map(({ styleId }, index) =>
              styleId ? (
                <TrimCard
                  makeSlug={styles?.[index]?.makeSlug}
                  modelSlug={styles?.[index]?.modelSlug}
                  year={styles?.[index]?.year}
                  isUsed={isUsedState(styles?.[index]?.publicationState)}
                  modelName={styles?.[index]?.modelName}
                  trimName={styles?.[index]?.trim}
                  engine={getStyleName(styles?.[index]?.name)?.engine}
                  baseMSRP={pricing?.[index]?.baseMSRP}
                  esp={pricing?.[index]?.tmv}
                  inventoryCount={trimsInventoryCount?.[styles?.[index]?.trim]}
                  onRemoveTrim={onRemoveTrim}
                  isRecommended={makeNiceName(styles?.[index]?.trim) === recommendedTrim}
                />
              ) : (
                <TrimAddCard onAddTrim={onAddTrim} removedTrims={removedTrims} />
              )
            ),
          },
        ],
      },
      {
        alternateFeaturesBgColor: false,
        features: [
          {
            rowLabel: 'Hide similarities',
            cellClassName: 'hide-similarities-cell px-0 border-0 pt-1_25',
            data: [<HideSimilaritiesSwitch hideSimilar={hideSimilar} toggleHideSimilar={toggleHideSimilar} />],
          },
        ],
      },
      // end heading
      ...trimsFeaturesMap
        .filter(({ isEvSection }) => !isEvSection || hasEvSection)
        .map(({ values, camelName: categorySlug, name: categoryName }) => ({
          heading: categoryName,
          features: values.map(value => ({
            label: getFeatureColumns({
              evComparableFeatures,
              columns,
              feature: value,
            }).map(({ feature: { name, displayName } }) => displayName || name),
            data: getFeatureColumns({ evComparableFeatures, columns, feature: value }).map(
              (
                {
                  column,
                  feature: {
                    name,
                    displayName,
                    camelName: nameSlug,
                    categoryCamelName: customCategorySlug,
                    forceValue,
                    blank,
                    optional,
                    isEvData,
                  },
                },
                index
              ) => {
                let featureValue = isEvData
                  ? get(evComparableFeatures, `[${index}][${nameSlug}]`, NOT_AVAILABLE)
                  : get(comparableFeatures, `[${index}][${categoryName}][${name}]`, '');

                if (!nameSlug) {
                  featureValue = `<span class="medium">Not applicable</span>`;
                }

                if (forceValue) {
                  featureValue = forceValue;
                }

                const noValue = blank || (!hasFeatureValue(featureValue) && optional);

                return (
                  !!column.styleId &&
                  !noValue &&
                  (!!column.driveTrainOptions?.length && nameSlug === 'driveTrain' ? (
                    <DrivetrainFeatureField
                      value={featureValue}
                      name={name}
                      options={column.driveTrainOptions}
                      setTrimStyle={setTrimStyle}
                    />
                  ) : (
                    withHash(
                      <FeatureField
                        value={numberWithCommasForString(featureValue)}
                        featureCategorySlug={customCategorySlug || categorySlug}
                        featureNameSlug={nameSlug}
                        name={displayName || name}
                        style={column}
                        buttonClass={classnames({ 'size-16': !isSmall })}
                      />,
                      `${name}-${featureValue}`
                    )
                  ))
                );
              }
            ),
            blank: getFeatureColumns({ evComparableFeatures, columns, feature: value }).map(
              ({ column, feature: { blank } }) => !!(column.styleId && blank)
            ),
          })),
        })),
      {
        heading: '5-Year Ownership Costs',
        features: map(tcoMap, (name, niceName) => ({
          label: name,
          data: styles.map(
            (style, index) =>
              !!get(style, 'id') &&
              withHash(
                <Fragment>
                  {typeof get(tco, `[${index}][${niceName}]`) === 'number'
                    ? formatPriceString(get(tco, `[${index}][${niceName}]`), 0, 2)
                    : NOT_AVAILABLE}
                </Fragment>,
                get(tco, `[${index}][${niceName}]`)
              )
          ),
        })),
      },
      {
        alternateFeaturesBgColor: false,
        heading: 'Colors',
        features: [
          {
            label: 'Interior',
            data: columns.map(
              ({ styleId, name }, index) =>
                !!styleId &&
                withHash(
                  <CarColorSection
                    idPrefix={name}
                    type="interior"
                    colors={get(colors, `[${index}].INTERIOR`)}
                    isSmall={isSmall}
                  />,
                  [
                    'interior',
                    get(colors, `[${index}].INTERIOR`)?.map(({ color }) => `${color?.r},${color?.g},${color?.b}`),
                  ].join()
                )
            ),
          },
          {
            label: 'Exterior',
            data: columns.map(
              ({ styleId, name }, index) =>
                !!styleId &&
                withHash(
                  <CarColorSection
                    idPrefix={name}
                    type="exterior"
                    colors={get(colors, `[${index}].EXTERIOR`)}
                    isSmall={isSmall}
                  />,
                  [
                    'exterior',
                    get(colors, `[${index}].EXTERIOR`)?.map(({ color }) => `${color?.r},${color?.g},${color?.b}`),
                  ].join()
                )
            ),
          },
        ],
      },
    ];

    return {
      stickyCarHeaderRenderMap,
      renderMap,
    };
  }, [
    colors,
    columns,
    comparableFeatures,
    comparatorHeaderRef,
    evComparableFeatures,
    hideSimilar,
    isSmall,
    onAddTrim,
    onRemoveTrim,
    pricing,
    recommendedTrim,
    removedTrims,
    setTrimStyle,
    styles,
    tco,
    toggleHideSimilar,
    trimsInventoryCount,
  ]);

  if (!comparableFeatures) {
    return null;
  }

  return (
    <div className="trims-comparator-table">
      <ContentTable
        tableRef={tableRef}
        renderMap={contentData.renderMap}
        colNumber={columns.length}
        keyPrefix="features"
        navSections={SUB_NAV_SECTIONS_MAP}
        noBorders={false}
        hideSimilar={hideSimilar}
        navListCustomEvent={NAV_LIST_CUSTOM_EVENT}
        allowDesktopOverflow
      />
      <AnimatedSticky
        className="heading-row"
        onStateChange={onStickyStateChange}
        intersectionRef={comparatorHeaderRef}
        hideSticky={!isTableVisible}
        stickyClassName="px-0"
        stickyFragment={
          <div className="sticky-header-wrapper px-0_5" aria-hidden>
            {!isSmall && <div className="heading-2 pt-1 px-xl-0_25 mb-0 text-start">{title}</div>}
            <ComparatorAnchorNav customOffset={customOffset} list={navList} ariaLabel="Comparison table sticky" />
            <div className="sticky-table-wrapper pos-r">
              <div className="overflow-x-hidden px-0_5" ref={comparatorHeaderStickyRef}>
                <ContentTable
                  renderMap={contentData.stickyCarHeaderRenderMap}
                  colNumber={columns.length}
                  keyPrefix="sticky-header"
                  hideSimilar={hideSimilar}
                  noBorders
                  allowDesktopOverflow
                />
              </div>
              <ClientSideRender>
                <ScrollArrows heightRef={comparatorHeaderStickyRef} onArrowClick={onArrowClick} arrows={arrows} />
              </ClientSideRender>
            </div>
          </div>
        }
      />
    </div>
  );
}

TrimsComparatorTableUI.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      styleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  navList: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, hash: PropTypes.string })).isRequired,
  onStickyStateChange: PropTypes.func.isRequired,
  setTrimStyle: PropTypes.func.isRequired,
  onAddTrim: PropTypes.func.isRequired,
  onRemoveTrim: PropTypes.func.isRequired,
  arrows: PropTypes.shape({ left: PropTypes.bool, right: PropTypes.bool }).isRequired,
  onArrowClick: PropTypes.func.isRequired,
  styles: PropTypes.arrayOf(VehicleEntities.Style),
  removedTrims: PropTypes.arrayOf(PropTypes.string),
  comparableFeatures: PropTypes.arrayOf(
    PropTypes.shape({
      exteriorFeatures: PropTypes.objectOf(PropTypes.string),
      interiorFeatures: PropTypes.objectOf(PropTypes.string),
      safety: PropTypes.objectOf(PropTypes.string),
      specifications: PropTypes.objectOf(PropTypes.string),
      warranty: PropTypes.objectOf(PropTypes.string),
    })
  ),
  evComparableFeatures: PropTypes.arrayOf(PropTypes.object),
  pricing: PropTypes.arrayOf(
    PropTypes.shape({
      tmv: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      baseMSRP: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      baseInvoice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  tco: PropTypes.arrayOf(
    PropTypes.shape({
      federalTaxCredit: PropTypes.number,
      insurance: PropTypes.number,
      maintenance: PropTypes.number,
      repairs: PropTypes.number,
      taxesAndFees: PropTypes.number,
      financeInterest: PropTypes.number,
      depreciation: PropTypes.number,
      fuel: PropTypes.number,
      tcoPrice: PropTypes.number,
      averageCostPerMile: PropTypes.number,
    })
  ),
  colors: PropTypes.arrayOf(
    PropTypes.shape({
      EXTERIOR: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          color: PropTypes.shape({
            r: PropTypes.number,
            g: PropTypes.number,
            b: PropTypes.number,
          }),
        })
      ),
      INTERIOR: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          color: PropTypes.shape({
            r: PropTypes.number,
            g: PropTypes.number,
            b: PropTypes.number,
          }),
        })
      ),
    })
  ),
  trimsInventoryCount: PropTypes.objectOf(PropTypes.number),
  recommendedTrim: PropTypes.string,
  customOffset: PropTypes.number,
  comparatorHeaderRef: PropTypes.shape(),
  comparatorHeaderStickyRef: PropTypes.shape(),
  isSmall: PropTypes.bool,
};

export const propsAreEqual = (prevProps, { comparableFeatures, pricing }) => !pricing || !comparableFeatures;

export const TrimsComparatorTable = connectToModel(React.memo(TrimsComparatorTableUI, propsAreEqual), {
  comparableFeatures: bindToPath(
    ({ columns }) => !!columns?.length && `comparableFeatures["${getColumnIdsString(columns)}"]`,
    ComparatorModel
  ),
  evComparableFeatures: bindToPath(
    ({ columns }) => !!columns?.length && `evEfficiency["${getColumnIdsString(columns)}"]`,
    ComparatorModel
  ),
  pricing: bindToPath(
    ({ columns }) => !!columns?.length && `pricing["${getColumnIdsString(columns)}"]`,
    ComparatorModel
  ),
  tco: bindToPath(({ styles }) => !!styles?.length && `tco["${getTCOIdsString(styles)}"]`, ComparatorModel),
  colors: bindToPath(({ columns }) => !!columns?.length && `colors["${getColumnIdsString(columns)}"]`, ComparatorModel),
  trimsInventoryCount: bindToPath(
    ({ vehicleParams }) => buildTrimsInventoryPath(vehicleParams),
    InventoryAggregateModel
  ),
  recommendedTrim: bindToPath(({ vehicleParams }) => buildRecommendedTrimPath(vehicleParams), EditorialReviewModel),
});
